import React, { useState, memo } from 'react';
import { TEMPLATE_REGISTRY } from '../../../templates/templateDesignSystem';
import ScaledResumePreview from '../preview/ScaledResumePreview';
import ATSRing from './ATSRing';
import { Heart } from 'lucide-react';

const TemplateCard = memo(({ templateId, isActive, isRecommended, onClick, globalAccent, T, resumeData, isFavorite, onToggleFavorite }) => {
  const [hovered, setHovered] = useState(false);
  const meta = TEMPLATE_REGISTRY[templateId];
  if (!meta) return null;

  const accent = globalAccent || meta.accent;
  const catColors = {
    ats_verified: { bg: '#05966912', border: '#05966928', text: '#059669' },
    student: { bg: '#0369a112', border: '#0369a128', text: '#0ea5e9' },
    professional: { bg: '#7c3aed12', border: '#7c3aed28', text: '#8b5cf6' },
  };
  const hasSidebar = !!meta.features?.sidebar;
  const hasPhoto = !!meta.features?.photo;
  const layoutDesc = hasSidebar ? '2 Columns (Sidebar)' : '1 Column (Standard)';
  const tooltipText = `${meta.name || meta.label}\nATS Rating: ${meta.atsRating || 95}%\nComplexity: ${meta.complexity || 'easy'}\nLayout: ${layoutDesc}\nSidebar: ${hasSidebar ? 'Yes' : 'No'}\nPhoto: ${hasPhoto ? 'Optional' : 'No'}\nBest For: ${meta.recommendation?.roles?.join(', ')}`;

  return (
    <div
      className="tg-card"
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      title={tooltipText}
      style={{
        cursor: 'pointer',
        borderRadius: 0,
        overflow: 'hidden',
        position: 'relative',
        background: isActive ? T.cardActiveBg : hovered ? T.cardHoverBg : T.cardBg,
        border: `1.5px solid ${isActive ? accent : hovered ? T.borderHover : T.borderCard}`,
        boxShadow: isActive
          ? `0 0 0 1px ${accent}22, 0 8px 32px ${accent}18, ${T.hoverShadow}`
          : hovered ? T.hoverShadow : T.cardShadow,
      }}
    >
      {/* Recommended ribbon */}
      {isRecommended && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          zIndex: 10,
          background: 'linear-gradient(135deg, #f59e0b, #ef4444)',
          padding: '4px 12px 4px 10px',
          borderRadius: '14px 0 14px 0',
          fontSize: '7.5px',
          fontWeight: 900,
          color: 'white',
          letterSpacing: '0.4px',
          boxShadow: '0 2px 8px rgba(239,68,68,0.35)',
        }}>⭐ RECOMMENDED</div>
      )}

      {/* Resume Preview */}
      <div style={{
        margin: '10px 10px 0',
        borderRadius: 10,
        overflow: 'hidden',
        background: 'white',
        boxShadow: T.previewShadow + ', inset 0 0 0 0.5px rgba(0,0,0,0.07)',
        aspectRatio: '210/250', // Shorter aspect ratio to reduce card height
        position: 'relative',
      }}>
        {/* Heart Favorite button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleFavorite(templateId);
          }}
          style={{
            position: 'absolute',
            top: 8,
            left: 8,
            zIndex: 15,
            width: 24,
            height: 24,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.85)',
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            color: isFavorite ? '#ef4444' : '#64748b',
            outline: 'none',
          }}
          title={isFavorite ? "Remove from Favorites" : "Add to Favorites"}
        >
          <Heart size={12} fill={isFavorite ? '#ef4444' : 'none'} />
        </button>
        {meta.assets && meta.assets.card ? (
          <>
            <img
              src={meta.assets.card}
              alt={meta.label}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
              }}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                const fallbackContainer = e.currentTarget.nextSibling;
                if (fallbackContainer) {
                  fallbackContainer.style.display = 'block';
                }
              }}
            />
            <div style={{ display: 'none', width: '100%', height: '100%' }}>
              <ScaledResumePreview templateId={templateId} accentColor={accent} resumeData={resumeData} />
            </div>
          </>
        ) : (
          <ScaledResumePreview templateId={templateId} accentColor={accent} resumeData={resumeData} />
        )}

        {/* Hover overlay */}
        <div className="tg-hover-overlay" style={{
          position: 'absolute',
          inset: 0,
          opacity: 0,
          background: 'linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 55%)',
          transition: 'opacity 0.2s',
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'center',
          paddingBottom: 10,
        }}>
          <span style={{
            fontSize: '9px',
            fontWeight: 700,
            color: 'white',
            background: 'rgba(0,0,0,0.4)',
            backdropFilter: 'blur(8px)',
            padding: '4px 14px',
            borderRadius: 20,
            border: '1px solid rgba(255,255,255,0.2)',
          }}>Click to Use</span>
        </div>

        {/* Active checkmark */}
        {isActive && (
          <div style={{
            position: 'absolute',
            top: 8,
            right: 8,
            width: 24,
            height: 24,
            borderRadius: '50%',
            background: accent,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 2px 10px ${accent}90`,
          }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
            </svg>
          </div>
        )}
      </div>

      {/* Card body */}
      <div style={{ padding: '10px 12px 14px', display: 'flex', flexDirection: 'column', gap: 7 }}>
        {/* Name row + ATS ring */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3 style={{
              fontSize: '11.5px',
              fontWeight: 800,
              margin: 0,
              lineHeight: 1.25,
              letterSpacing: '-0.2px',
              color: isActive ? accent : T.textPrimary,
            }}>{meta.label}</h3>
          </div>
          <ATSRing score={meta.atsRating} color={meta.atsColor} label={meta.atsLabel.split(' ')[1] || 'ATS'} size={46} />
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px 4px' }}>
          {meta.bestForTags.map((tag, i) => (
            <span key={i} style={{
              fontSize: '7.5px',
              fontWeight: 600,
              padding: '2px 8px',
              borderRadius: 20,
              background: `${accent}12`,
              border: `1px solid ${accent}25`,
              color: isActive ? accent : T.textSecondary,
            }}>✓ {tag}</span>
          ))}
        </div>
      </div>
    </div>
  );
});

export default TemplateCard;
